import { monaco } from "@via/components/code-editor/monaco.ts";

export interface CortexMonacoTarget {
  editor: {
    executeEdits: (
      source: string,
      edits: Array<{ range: unknown; text: string; forceMoveMarkers?: boolean }>
    ) => void;
    getSelection?: () =>
      | {
          startLineNumber: number;
          startColumn: number;
          endLineNumber: number;
          endColumn: number;
        }
      | null
      | undefined;
    getPosition?: () =>
      | {
          lineNumber: number;
          column: number;
        }
      | null
      | undefined;
    focus?: () => void;
  } | null;
  pageUrl: string;
  updatedAt: number;
}

let cached: CortexMonacoTarget | null = null;
const listeners = new Set<() => void>();

function emitChange(): void {
  for (const listener of listeners) listener();
}

function getCurrentPageUrl(): string {
  if (typeof window === "undefined") return "";
  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

export function subscribeCortexMonacoTarget(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getCortexMonacoTarget(): CortexMonacoTarget | null {
  return cached;
}

export function getCortexMonacoTargetForCurrentPage(): CortexMonacoTarget | null {
  const target = getCortexMonacoTarget();
  if (!target) return null;
  return target.pageUrl === getCurrentPageUrl() ? target : null;
}

export function setCortexMonacoTarget(
  editor: CortexMonacoTarget["editor"]
): void {
  cached = {
    editor,
    pageUrl: getCurrentPageUrl(),
    updatedAt: Date.now(),
  };
  emitChange();
}

export function clearCortexMonacoTarget(): void {
  cached = null;
  emitChange();
}

export function applyToCortexMonacoTarget(text: string): boolean {
  const target = getCortexMonacoTargetForCurrentPage();
  const editor = target?.editor;
  if (!editor) return false;

  const selection = editor.getSelection?.() ?? null;
  const position = editor.getPosition?.() ?? null;
  if (!selection && !position) return false;

  const range = selection
    ? new monaco.Range(
        selection.startLineNumber,
        selection.startColumn,
        selection.endLineNumber,
        selection.endColumn
      )
    : new monaco.Range(
        position!.lineNumber,
        position!.column,
        position!.lineNumber,
        position!.column
      );

  editor.executeEdits("", [
    {
      range,
      text,
      forceMoveMarkers: true,
    },
  ]);
  editor.focus?.();
  return true;
}
